import React, { useState, useEffect } from "react";
import { TailSpin } from "react-loader-spinner";
import axios from "axios";
import "../styles/EmailManagement.css";

const EmailManagement = ({ report, onClose, onUpdateEmails, userEmail }) => {
  const [currentEmails, setCurrentEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailsToAdd, setEmailsToAdd] = useState([]);
  const [emailsToRemove, setEmailsToRemove] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [emailsFromSettings, setEmailsFromSettings] = useState(false); // NEW: Track if emails came from settings
  const [emailSource, setEmailSource] = useState({});

  // Initialize the component with current emails
  useEffect(() => {
    if (report && report.shared_with) {
      setCurrentEmails([...report.shared_with]);
    }
  }, [report]);

  useEffect(() => {
    const fetchEmailSettings = async () => {
      try {
        if (!report?.shared_with || report.shared_with.length === 0) {
          const response = await axios.get("/api/email-settings", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

          if (response.data.success && response.data.emailRecipients) {
            const emails = response.data.emailRecipients
              .split(",")
              .map((email) => email.trim())
              .filter((email) => email !== "");

            // Mark these emails as coming from previous settings
            const sourceMap = {};
            emails.forEach((email) => {
              sourceMap[email] = "previous";
            });

            setEmailSource(sourceMap);
            setEmailsFromSettings(true); // Set flag for emails from settings
            setCurrentEmails(emails);
            setEmailsToAdd(emails); // Auto-add them for convenience
          }
        } else {
          // Mark these emails as already shared
          const sourceMap = {};
          report.shared_with.forEach((email) => {
            sourceMap[email] = "shared";
          });

          setEmailSource(sourceMap);
          setCurrentEmails([...report.shared_with]);
        }
      } catch (error) {
        console.error("Error fetching email settings:", error);
      }
    };

    fetchEmailSettings();
  }, [report]);

  // Validate email format
  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Handle adding a new email
  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();

    // Prevent adding user's own email
    if (trimmedEmail.toLowerCase() === userEmail.toLowerCase()) {
      setEmailError("You cannot share the report with yourself.");
      return;
    }

    // Rest of existing validation...
    if (!isValidEmail(trimmedEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    // Check if email is already in the list
    if (
      currentEmails.includes(trimmedEmail) ||
      emailsToAdd.includes(trimmedEmail)
    ) {
      setEmailError("This email is already in the list.");
      return;
    }

    // Add to emailsToAdd list
    setEmailsToAdd([...emailsToAdd, trimmedEmail]);

    // Remove from emailsToRemove if it was there
    setEmailsToRemove(emailsToRemove.filter((email) => email !== trimmedEmail));

    // Update the current display
    setCurrentEmails([...currentEmails, trimmedEmail]);

    // Clear input and error
    setEmailInput("");
    setEmailError("");
  };

  // Handle removing an email
  const handleRemoveEmail = (emailToRemove) => {
    // Update currentEmails for UI
    setCurrentEmails(currentEmails.filter((email) => email !== emailToRemove));

    // If this was an email we were going to add, just remove it from that list
    if (emailsToAdd.includes(emailToRemove)) {
      setEmailsToAdd(emailsToAdd.filter((email) => email !== emailToRemove));
    }
    // Otherwise, add to the remove list if it's an original email
    else if (report.shared_with && report.shared_with.includes(emailToRemove)) {
      setEmailsToRemove([...emailsToRemove, emailToRemove]);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onUpdateEmails(report.reportId, emailsToAdd, emailsToRemove);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle key press for email input
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && emailInput) {
      e.preventDefault();
      handleAddEmail();
    }
  };

  // Add this function to determine if save button should be enabled
  const isSaveEnabled = () => {
    // Enable if emails came from settings and there are emails to add
    if (emailsFromSettings && emailsToAdd.length > 0) {
      return true;
    }

    // Standard logic - enable if there are changes
    return emailsToAdd.length > 0 || emailsToRemove.length > 0;
  };

  return (
    <div className="email-modal-overlay" onClick={onClose}>
      <div className="email-modal" onClick={(e) => e.stopPropagation()}>
        <div className="email-modal-header">
          <h3>Manage Report Recipients</h3>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="email-modal-content">
          <p className="email-description">
            Add or remove people who should have access to this report. Each
            recipient will receive an email with a link to view the report.{" "}
            <a
              href="https://app.supademo.com/demo/cm9j58ysw366bljv5jy399h4b"
              className="learn-more-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Learn more about sharing reports
            </a>
          </p>

          <div className="email-input-section">
            <div className="email-input-container">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setEmailError("");
                }}
                onKeyPress={handleKeyPress}
                placeholder="Enter email address"
                className="email-input"
              />
              <button
                className="add-email-button"
                onClick={handleAddEmail}
                disabled={!emailInput.trim()}
              >
                Add
              </button>
            </div>
            {emailError && <div className="email-error">{emailError}</div>}
          </div>

          <div className="email-list-container">
            <h4>Current Recipients</h4>
            {currentEmails.length > 0 ? (
              <div className="email-list">
                {currentEmails.map((email, index) => (
                  <div className="email-item" key={index}>
                    <span className="email-text">
                      {email}
                      {emailSource[email] === "previous" && (
                        <span className="email-status">• Not shared yet</span>
                      )}
                    </span>
                    <button
                      className="remove-email-button"
                      onClick={() => handleRemoveEmail(email)}
                      title="Remove recipient"
                      disabled={isSaving}
                    >
                      &times;
                    </button>
                  </div>
                ))}

                {emailsToAdd.length > 0 &&
                  emailsToAdd.some(
                    (email) => emailSource[email] === "previous"
                  ) && (
                    <div className="helper-message">
                      Recipients marked "Not shared yet" were previously used
                      but don't have access to this report. Click Save to send
                      them access.
                    </div>
                  )}
              </div>
            ) : (
              <p className="no-emails-message">
                No recipients for this report.
              </p>
            )}
          </div>
        </div>

        <div className="email-modal-footer">
          <div className="changes-summary">
            {emailsFromSettings && emailsToAdd.length > 0 ? (
              <div className="settings-notice">
                <i className="fas fa-info-circle"></i>
                <p>
                  <strong>{emailsToAdd.length} recipient(s)</strong> loaded from
                  your default settings. Click Save to share the report with
                  them.
                </p>
              </div>
            ) : (
              <>
                {emailsToAdd.length > 0 && (
                  <p className="add-notice">
                    <i className="fas fa-user-plus"></i>
                    <span>Adding {emailsToAdd.length} new recipient(s)</span>
                  </p>
                )}
                {emailsToRemove.length > 0 && (
                  <p className="remove-notice">
                    <i className="fas fa-user-minus"></i>
                    <span>Removing {emailsToRemove.length} recipient(s)</span>
                  </p>
                )}
              </>
            )}
          </div>
          <div className="button-group">
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="save-button"
              onClick={handleSubmit}
              disabled={!isSaveEnabled() || isSaving}
            >
              {isSaving ? (
                <div className="loading-button-content">
                  <TailSpin color="#ffffff" height={16} width={16} />
                  <span>
                    {emailsToAdd.length > 0 ? "Sending emails..." : "Saving..."}
                  </span>
                </div>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default EmailManagement;
